import { getVehiclePosition } from "mobility-toolbox-js/ol";
import { linear } from "ol/easing";
import { memo, useEffect, useRef } from "react";

import { setIsFollowingVehicleLoading, setStation } from "../model/actions";
import { getStationStatus } from "../utils";
import useAppDispatch from "../utils/hooks/useAppDispatch";
import useAppSelector from "../utils/hooks/useAppSelector";

import type {
  RealtimeStationproperties,
  RealtimeTrajectoryProperties,
} from "mobility-toolbox-js/types";
import type { Feature } from "ol";
import type { Coordinate } from "ol/coordinate";
import type { Geometry } from "ol/geom";

type StationFeature = Feature<Geometry, RealtimeStationproperties>;

type VehicleWithDeparture = {
  departure?: { train_id?: string };
} & RealtimeTrajectoryProperties;

interface StationWithStatus {
  stationId: string;
  status: { isNextStop?: boolean };
}

/**
 * Follows the selected vehicle by re-centering the map on its position.
 * Extracted from LiveMap's onIsFollowingVehicleChange/centerOnTrajectory/
 * isVehicleCloseToStation.
 */
function IsFollowingVehicleChangeListener() {
  const dispatch = useAppDispatch();
  const auto = useAppSelector((state) => state.auto);
  const isFollowingVehicle = useAppSelector(
    (state) => state.isFollowingVehicle,
  );
  const map = useAppSelector((state) => state.map);
  const mode = useAppSelector((state) => state.mode);
  const realtimeLayer = useAppSelector((state) => state.realtimeLayer);
  const selectedVehicles = useAppSelector((state) => state.selectedVehicles);
  const station = useAppSelector((state) => state.station);
  const stationsLayer = useAppSelector((state) => state.stationsLayer);
  const stopSequence = useAppSelector((state) => state.stopSequence);
  // onIsFollowingVehicleChange was only called from componentDidUpdate, never
  // on mount.
  const isMountedRef = useRef(false);
  // centerOnTrajectory/isVehicleCloseToStation must always read the latest
  // value (like `this.props.x` did in the original class), not the one
  // captured when the effect ran.
  const selectedVehiclesRef = useRef(selectedVehicles);
  const stopSequenceRef = useRef(stopSequence);
  const realtimeLayerRef = useRef(realtimeLayer);
  const mapRef = useRef(map);
  const stationRef = useRef(station);
  const stationsLayerRef = useRef(stationsLayer);
  const autoRef = useRef(auto);
  const modeRef = useRef(mode);

  useEffect(() => {
    selectedVehiclesRef.current = selectedVehicles;
  }, [selectedVehicles]);

  useEffect(() => {
    autoRef.current = auto;
  }, [auto]);

  useEffect(() => {
    mapRef.current = map;
  }, [map]);

  useEffect(() => {
    realtimeLayerRef.current = realtimeLayer;
  }, [realtimeLayer]);

  useEffect(() => {
    stopSequenceRef.current = stopSequence;
  }, [stopSequence]);

  useEffect(() => {
    stationRef.current = station;
  }, [station]);

  useEffect(() => {
    stationsLayerRef.current = stationsLayer;
  }, [stationsLayer]);

  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return undefined;
    }

    if (!isFollowingVehicle) {
      return undefined;
    }

    const getStationsWithStatus = () =>
      (
        stopSequenceRef.current as unknown as {
          stationsWithStatus?: StationWithStatus[];
        } | null
      )?.stationsWithStatus;

    const isVehicleCloseToStation = () => {
      const stationsWithStatus = getStationsWithStatus();
      const idxNextStation = stationsWithStatus?.findIndex(
        (statio) => !!statio.status.isNextStop,
      );
      if (idxNextStation !== undefined && idxNextStation >= 0) {
        return !!(
          getStationStatus(stopSequenceRef.current, idxNextStation) as {
            isCloseToNextStop?: boolean;
          }
        ).isCloseToNextStop;
      }
      return false;
    };

    // Center the map on the vehicle we follow.
    const centerOnTrajectory = async (
      selectedVehicle: VehicleWithDeparture,
    ) => {
      const trainId =
        selectedVehicle.departure?.train_id ?? selectedVehicle.train_id;

      const trajectory = realtimeLayerRef.current?.trajectories?.[trainId];

      let center: Coordinate | undefined = trajectory?.properties.coordinate;

      if (!center) {
        const message = ((await realtimeLayerRef.current?.api?.getTrajectory(
          trainId,
          modeRef.current,
        )) ?? Promise.resolve({})) as { content?: unknown };

        if (message.content) {
          center = getVehiclePosition(
            Date.now(),
            message.content as Parameters<typeof getVehiclePosition>[1],
            true,
          )?.coord;
        }
      }

      if (!center) {
        return false;
      }

      const view = mapRef.current.getView();
      const zoom = view.getZoom();
      const resolution =
        zoom !== undefined && zoom > 0
          ? view.getResolutionForZoom(zoom)
          : undefined;

      view.cancelAnimations();

      const promise = new Promise<boolean>((resolve) => {
        view.animate(
          {
            center,
            duration: 1000,
            easing: linear,
            resolution,
          },
          (success) => {
            resolve(success);
          },
        );
      });

      // The auto mode always select the station close to the vehicle we follow.
      if (autoRef.current) {
        // If the close enough station detected is the next stop we select the station.
        const isCloseToStation = isVehicleCloseToStation();
        const nextStation = getStationsWithStatus()?.find(
          (statio) => !!statio.status.isNextStop,
        );

        if (isCloseToStation && !stationRef.current) {
          const uic = nextStation?.stationId;
          const features = stationsLayerRef.current
            .getSource()
            ?.getFeatures() as StationFeature[] | undefined;
          const closeStation = features?.find(
            (statio) => `${statio.get("uic")}` === `${uic}`,
          );

          if (!closeStation) {
            // eslint-disable-next-line no-console
            console.warn(
              `Fail to find the close station ${uic}`,
              stationRef.current,
            );
          }

          dispatch(setStation(closeStation));

          // If the close enough station detected is not the next stop we deselect the station.
        } else if (!isCloseToStation && stationRef.current) {
          dispatch(setStation());
        }
      }

      return promise;
    };

    let updateCenterInterval: number;

    const [selectedVehicle] =
      selectedVehiclesRef.current as VehicleWithDeparture[];

    dispatch(setIsFollowingVehicleLoading(true));
    void centerOnTrajectory(selectedVehicle)
      .then((success) => {
        if (success === true) {
          updateCenterInterval = window.setInterval(() => {
            const [latestSelectedVehicle] =
              selectedVehiclesRef.current as VehicleWithDeparture[];
            void centerOnTrajectory(latestSelectedVehicle);
          }, 1000);
        }
        return success;
      })
      .finally(() => {
        dispatch(setIsFollowingVehicleLoading(false));
      });

    return () => {
      clearInterval(updateCenterInterval);
    };
  }, [dispatch, isFollowingVehicle]);

  return null;
}

export default memo(IsFollowingVehicleChangeListener);
